import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
    ArrowLeft, ArrowRight, Building2, Check, CheckCircle2, FileText, Layers, Loader2, Lock, Minus, Search, Upload, Users, X,
} from 'lucide-react';
import { Dialog, DialogContent, DialogDescription, DialogTitle } from '../ui/dialog';
import { lmError, lmErrorCode, newIdempotencyKey } from './lmApi';
import LmFieldCombobox from './LmFieldCombobox';
import LmAddCustomFieldDialog from './LmAddCustomFieldDialog';
import LmPreviewExpanded, { PreviewTable } from './LmPreviewExpanded';

/**
 * Data Import Wizard (#769 LM-03) — the prototype's five steps:
 * Import → Select Fields → Map Fields → Preview Data → Confirm.
 *
 * Select Fields lists the uploaded file's own columns (#971); Map Fields maps
 * only the selected ones, to any field of the catalog or to a custom field
 * created on the spot (#972); Preview shows every mapped column inline or
 * expanded (#973). A column left unselected never reaches Preview or staging.
 *
 * Records land in Imports as read-only; nothing in the wizard writes the CRM.
 * The mapping is saved (versioned) before every preview, and a preview is
 * bound to that mapping version: changing the mapping or the duplicate
 * policy re-runs the preview before Confirm is allowed.
 *
 * "One-time owner migration" (#862, shown only with `contacts:migrate_owner`)
 * uploads one part of the Contact Owner migration: it maps a contact id or
 * email plus exactly one owner column, and its rows change the owner of
 * existing contacts only — after a different admin approves the migration.
 */
const STEPS = ['Import', 'Select Fields', 'Map Fields', 'Preview Data', 'Confirm'];
const NEXT_LABEL = { 1: 'Next: Select Fields', 2: 'Next: Map Fields', 3: 'Next: Preview Data', 4: 'Next: Confirm', 5: 'Start import' };
const DUPLICATE_OPTIONS = [
    ['skip', 'Skip duplicates', 'Keep the existing CRM record, ignore the incoming row.'],
    ['update', 'Update existing', 'Link to the existing CRM record and write the import’s values.'],
    ['import_as_new', 'Import as new', 'Create a separate record (ambiguous matches still need review).'],
];
const SOURCE_ICON = { csv: Upload, pitchbook: Building2, apollo: Users, zoominfo: Layers, crunchbase: Layers };
/** The contact identity column a migration may map besides the migration fields. */
const CONTACT_EMAIL_FIELD = { key: 'personal_email', label: 'Contact email', example: 'jane@acme.com' };
/** Preview Data shows this many mapped columns inline; the rest are behind Expand (#973). */
const INLINE_PREVIEW_COLUMNS = 6;
const SAMPLE_SEPARATOR = '  ·  ';

/** Up to three distinct non-empty values of a column from the sample rows (or its stats). */
function sampleValues(header, sampleRows, stats) {
    const out = [];
    const take = (v) => {
        const text = String(v ?? '').trim();
        if (text && !out.includes(text) && out.length < 3) out.push(text);
    };
    sampleRows.forEach((r) => take(r?.[header]));
    if (!out.length) (stats?.distinct || []).forEach(take);
    return out;
}

/**
 * The wizard's checkbox look: `state` is true (ticked), false, or 'mixed' (the Select Fields
 * header while only some of the shown columns are ticked). Theme tokens, so it reads in Graphite.
 */
function Box({ state }) {
    const on = Boolean(state);
    return (
        <span className={`shrink-0 w-4 h-4 rounded inline-flex items-center justify-center ${on ? 'border bg-primary border-primary text-primary-foreground' : 'border-[1.5px] bg-card border-muted-foreground/80'}`}
              aria-hidden="true">
            {state === 'mixed' ? <Minus className="w-[11px] h-[11px]" /> : on ? <Check className="w-[11px] h-[11px]" /> : null}
        </span>
    );
}

function Radio({ on }) {
    return (
        <span className={`block shrink-0 w-4 h-4 rounded-full box-border ${on ? 'border-[5px] border-primary' : 'border-[1.5px] border-muted-foreground/80'}`}
              aria-hidden="true" />
    );
}

function Tile({ label, value }) {
    return (
        <div className="border border-border rounded-[11px] px-3.5 py-3 bg-muted/50">
            <div className="text-[11px] font-semibold tracking-[0.05em] text-muted-foreground">{label}</div>
            <div className="mt-1.5 text-[19px] font-semibold text-foreground tabular-nums">{value}</div>
        </div>
    );
}

/** The preview's warnings as sentences. The required/invalid custom-value ones never block the import. */
function warningText(w, fieldLabel) {
    // The server words both variants (mapped-but-empty, and not mapped at all).
    if (w.code === 'required_custom_empty' && w.label) {
        if (w.message) return w.message;
        const n = Number(w.rows) || 0;
        return `Required field “${w.label}” is empty in ${n.toLocaleString()} row${n === 1 ? '' : 's'}.`;
    }
    if (w.code === 'custom_value_invalid') return w.message || `“${fieldLabel[w.field] || w.field}” has a value that does not fit its field type.`;
    return w.message;
}

export default function LmImportWizard({ open, onClose, api, onImported, canMigrateOwners = false }) {
    const [step, setStep] = useState(1);
    const [mode, setMode] = useState('csv');
    // The owner migration (#862): its parent operation and which part this upload is.
    const [migration, setMigration] = useState(null);
    const [migrationForm, setMigrationForm] = useState({ parts: '1', rows: '', reason: '', existing: '' });
    const [partIndex, setPartIndex] = useState(1);
    const isMigration = mode === 'migration';
    const [sources, setSources] = useState([]);
    const [catalog, setCatalog] = useState(null);
    const [draft, setDraft] = useState(null);
    const [duplicateOf, setDuplicateOf] = useState(null);
    const [sampleRows, setSampleRows] = useState([]);
    // Owner migration only: the migration fields ticked on its step 2.
    const [selected, setSelected] = useState(new Set());
    // Standard imports: the file's own columns that stay in the import (#971).
    const [selectedColumns, setSelectedColumns] = useState(new Set());
    const [selQuery, setSelQuery] = useState('');
    // The header whose "+ Add custom field" dialog is open (#972), and the note after a create.
    const [addFieldFor, setAddFieldFor] = useState(null);
    const [createdNote, setCreatedNote] = useState(null);
    const [columns, setColumns] = useState({});
    const [saveDefault, setSaveDefault] = useState(false);
    const [dupPolicy, setDupPolicy] = useState('skip');
    const [writePolicy, setWritePolicy] = useState('fill_blank');
    const [preview, setPreview] = useState(null);
    const [batchName, setBatchName] = useState('');
    const [pbEntity, setPbEntity] = useState('companies');
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState(null);
    const fileRef = useRef(null);
    // The Map Fields trigger of each column, so focus can return to it after the add dialog.
    const triggerRefs = useRef({});
    const addedFromRef = useRef(null);
    const confirmedRef = useRef(false);
    // One key per migration start, reused until it succeeds: a retry after a
    // lost response finds the parent it already created instead of a second one.
    const migrationKeyRef = useRef(null);

    const reset = useCallback(() => {
        setStep(1); setMode('csv'); setDraft(null); setDuplicateOf(null); setSampleRows([]);
        setMigration(null); setMigrationForm({ parts: '1', rows: '', reason: '', existing: '' }); setPartIndex(1);
        setSelected(new Set()); setSelectedColumns(new Set()); setSelQuery(''); setAddFieldFor(null); setCreatedNote(null);
        setColumns({}); setSaveDefault(false); setDupPolicy('skip');
        setWritePolicy('fill_blank'); setPreview(null); setBatchName(''); setBusy(false); setError(null);
        confirmedRef.current = false;
        migrationKeyRef.current = newIdempotencyKey();
    }, []);

    useEffect(() => {
        if (!open) return;
        reset();
        api.sources().then((r) => setSources(r.sources || [])).catch(() => setSources([]));
        api.fields().then(setCatalog).catch((e) => setError(lmError(e, 'Could not load the field list.')));
    }, [open, api, reset]);

    // Leaving Map Fields drops its one-off "field added" note.
    useEffect(() => { if (step !== 3) setCreatedNote(null); }, [step]);

    const close = () => {
        if (draft?.status === 'draft' && !confirmedRef.current && !duplicateOf) {
            api.discardDraft(draft.import_id).catch(() => {});
        }
        onClose?.();
    };

    const allFields = useMemo(() => {
        if (!catalog) return [];
        if (isMigration) return [CONTACT_EMAIL_FIELD, ...(catalog.migration_fields || [])];
        return [...(catalog.company_fields || []), ...(catalog.contact_fields || []), ...(catalog.relationship_fields || [])];
    }, [catalog, isMigration]);
    const fieldLabel = useMemo(() => Object.fromEntries(allFields.map((f) => [f.key, f.label])), [allFields]);
    const fieldMeta = useMemo(() => Object.fromEntries(allFields.map((f) => [f.key, f])), [allFields]);
    const headers = draft?.headers || [];
    const columnStats = draft?.column_stats || null;

    const adoptDraft = (res) => {
        const imp = res.import;
        if (res.duplicate && imp.status === 'available') {
            setDuplicateOf(imp);
            return;
        }
        setDraft(imp);
        // A different file starts over: the last file's preview, its "save as default" tick and
        // its error must not be read as belonging to this one.
        setPreview(null); setSaveDefault(false); setError(null);
        setSampleRows(res.sample_rows || []);
        setSelected(new Set(imp.mapping?.selected_fields || []));
        // A draft or saved mapping without selected_columns keeps every column (#971).
        const all = imp.headers || [];
        const saved = imp.mapping?.selected_columns;
        setSelectedColumns(new Set(Array.isArray(saved) ? saved.filter((h) => all.includes(h)) : all));
        setSelQuery('');
        setCreatedNote(null);
        setColumns(imp.mapping?.columns || {});
        setDupPolicy(imp.mapping?.duplicate_policy || 'skip');
        setWritePolicy(imp.mapping?.write_policy || 'fill_blank');
        setBatchName((imp.file_name || '').replace(/\.(csv|txt)$/i, ''));
    };

    const onFile = async (file) => {
        if (!file) return;
        if (isMigration && !migration) { setError('Start the owner migration first, then upload its parts.'); return; }
        setBusy(true); setError(null); setDuplicateOf(null);
        try {
            adoptDraft(await (isMigration
                ? api.uploadCsv(file, { operationId: migration.operation_id, partIndex })
                : api.uploadCsv(file)));
        } catch (err) {
            setError(lmError(err, 'The file could not be read.'));
        } finally {
            setBusy(false);
        }
    };

    const startMigration = async () => {
        setBusy(true); setError(null);
        try {
            if (migrationForm.existing.trim()) {
                const res = await api.ownerMigration(migrationForm.existing.trim());
                setMigration(res);
            } else {
                const res = await api.createOwnerMigration({
                    declared_parts: Number(migrationForm.parts) || 1,
                    declared_rows: Number(migrationForm.rows) || 0,
                    reason: migrationForm.reason,
                    idempotency_key: migrationKeyRef.current,
                });
                setMigration(res.operation);
                migrationKeyRef.current = newIdempotencyKey();
            }
            setPartIndex(1);
        } catch (err) {
            setError(lmError(err, 'The owner migration could not be started.'));
        } finally {
            setBusy(false);
        }
    };

    const loadPitchbook = async () => {
        setBusy(true); setError(null);
        try {
            const allowed = sources.find((t) => t.key === 'pitchbook')?.entities || [];
            const entity = allowed.length && !allowed.includes(pbEntity) ? allowed[0] : pbEntity;
            adoptDraft(await api.importPitchbook(entity));
        } catch (err) {
            setError(lmError(err, 'The PitchBook snapshot could not be loaded.'));
        } finally {
            setBusy(false);
        }
    };

    // Owner migration only: tick or untick one of its fixed fields.
    const toggleMigrationField = (key) => {
        setSelected((prev) => {
            const next = new Set(prev);
            if (next.has(key)) {
                next.delete(key);
                setColumns((cols) => Object.fromEntries(Object.entries(cols).map(([h, t]) => [h, t === key ? null : t])));
            } else {
                next.add(key);
            }
            return next;
        });
        setPreview(null);
    };

    // Select Fields (#971): tick or untick columns. Unticking drops the column's target.
    const setColumnsSelected = (list, on) => {
        setSelectedColumns((prev) => {
            const next = new Set(prev);
            list.forEach((h) => (on ? next.add(h) : next.delete(h)));
            return next;
        });
        if (!on) setColumns((cols) => Object.fromEntries(Object.entries(cols).map(([h, t]) => [h, list.includes(h) ? null : t])));
        setPreview(null);
    };

    const setColumn = (header, target) => {
        setColumns((cols) => ({ ...cols, [header]: target || null }));
        setCreatedNote(null);
        setPreview(null);
    };

    // The mapping as it is saved: only selected columns carry a target, and the
    // fields in use are derived from them (owner migration keeps its ticked fields).
    const mappingBody = () => {
        if (isMigration) return { selected_fields: [...selected], columns };
        const keep = headers.filter((h) => selectedColumns.has(h));
        const cols = Object.fromEntries(Object.entries(columns).map(([h, t]) => [h, selectedColumns.has(h) ? t || null : null]));
        return { selected_columns: keep, selected_fields: [...new Set(Object.values(cols).filter(Boolean))], columns: cols };
    };

    const runPreview = async (overrides = {}) => {
        if (!draft) return;
        setBusy(true); setError(null);
        try {
            await api.saveMapping(draft.import_id, {
                ...mappingBody(),
                duplicate_policy: overrides.dupPolicy ?? dupPolicy,
                write_policy: overrides.writePolicy ?? writePolicy,
                save_as_default: isMigration ? false : saveDefault,
            });
            setPreview(await api.preview(draft.import_id));
        } catch (err) {
            setError(lmError(err, 'The preview could not be built.'));
            setPreview(null);
        } finally {
            setBusy(false);
        }
    };

    const confirm = async () => {
        if (!draft || !preview) return;
        setBusy(true); setError(null);
        try {
            const imp = await api.confirm(draft.import_id, { preview_id: preview.preview_id, batch_name: batchName });
            confirmedRef.current = true;
            onImported?.(imp);
            onClose?.();
        } catch (err) {
            if (lmErrorCode(err) === 'preview_stale') {
                setStep(4);
                await runPreview();  // clears the error first — say why only after it rebuilt
                setError('The preview expired — it was rebuilt. Review it and start the import again.');
            } else {
                setError(lmError(err, 'The import could not be started.'));
            }
        } finally {
            setBusy(false);
        }
    };

    const canNext = {
        // A draft from the other mode (switched after uploading) cannot continue.
        1: !!draft && draft.status === 'draft' && (draft.mode === 'owner_migration') === isMigration,
        2: isMigration ? selected.size > 0 : selectedColumns.size > 0,
        3: Object.entries(columns).some(([h, t]) => t && selectedColumns.has(h)),
        4: !!preview && !preview.blocking,
        5: !!preview && !busy,
    }[step];

    const next = async () => {
        if (step === 3) { setStep(4); await runPreview(); return; }
        if (step === 5) { await confirm(); return; }
        setStep((s) => Math.min(5, s + 1));
    };

    // Map Fields lists only the selected columns; a target held by one column is taken for the others.
    const mappedHeaders = headers.filter((h) => selectedColumns.has(h));
    const usedBy = {};
    mappedHeaders.forEach((h) => { if (columns[h]) usedBy[columns[h]] = h; });
    const mappedCount = mappedHeaders.filter((h) => columns[h]).length;
    const selectedList = allFields.filter((f) => selected.has(f.key));

    // The whole catalog, grouped (#971). A custom field is listed under CUSTOM FIELDS only.
    const plain = (list) => (list || []).filter((f) => !f.is_custom);
    const fieldGroups = !catalog ? [] : isMigration
        ? [{ id: 'migration', label: 'OWNER MIGRATION FIELDS', fields: selectedList }]
        : [
            { id: 'company', label: 'COMPANY', fields: plain(catalog.company_fields) },
            { id: 'contact', label: 'CONTACT', fields: plain(catalog.contact_fields) },
            { id: 'relationship', label: 'RELATIONSHIP', fields: plain(catalog.relationship_fields) },
            { id: 'custom', label: 'CUSTOM FIELDS', fields: [...(catalog.company_fields || []), ...(catalog.contact_fields || [])].filter((f) => f.is_custom) },
        ];

    // What Preview shows: the mapped targets in the file's column order.
    const previewColumns = isMigration
        ? selectedList.map((f) => ({ key: f.key, label: f.label }))
        : mappedHeaders.filter((h) => columns[h]).map((h) => ({
            key: columns[h], label: fieldLabel[columns[h]] || columns[h], isCustom: !!fieldMeta[columns[h]]?.is_custom,
        }));

    // "+ Add custom field" (#972): the dialog created the field; refetch the catalog, map the column, say so.
    const onCustomFieldCreated = async (field, entity) => {
        const header = addFieldFor;
        const key = field.catalog_key || `cf_${field.key}`;
        let fresh = null;
        try {
            fresh = await api.fields();
        } catch {
            // Fall back to the field the server just returned, so the column can still be mapped.
            const entry = { key, label: field.label, type: field.field_type, entity, required: !!field.required, is_custom: true, options: field.options || [] };
            const list = entity === 'account' ? 'company_fields' : 'contact_fields';
            fresh = { ...catalog, [list]: [...((catalog || {})[list] || []), entry] };
        }
        setCatalog(fresh);
        setColumns((cols) => ({ ...cols, [header]: key }));
        setPreview(null);
        const noun = entity === 'account' ? ['Companies', 'companies'] : ['Contacts', 'contacts'];
        setCreatedNote(`“${field.label}” was added to ${noun[0]} and mapped to ${header}. Existing ${noun[1]} show it empty.`);
        setAddFieldFor(null);
    };

    const mapExistingField = (key) => {
        const header = addFieldFor;
        setColumn(header, key);
        setAddFieldFor(null);
    };

    // Select Fields rows: the search narrows what Select all / Clear act on.
    const query = selQuery.trim().toLowerCase();
    const visibleColumns = headers.map((h, index) => ({ h, index })).filter(({ h }) => !query || h.toLowerCase().includes(query));
    const visibleSelected = visibleColumns.filter(({ h }) => selectedColumns.has(h)).length;
    const headerState = !visibleColumns.length || visibleSelected === 0 ? false : visibleSelected === visibleColumns.length ? true : 'mixed';

    return (
        <Dialog open={open} onOpenChange={(v) => { if (!v && !busy) close(); }}>
            <DialogContent
                className="sm:max-w-[1060px] w-[calc(100vw-40px)] h-[calc(100vh-48px)] max-h-[calc(100vh-48px)] p-0 gap-0 overflow-hidden flex flex-col rounded-[14px] [&>button.absolute]:hidden"
                data-testid="lm-import-wizard">
                <div className="shrink-0 flex items-start gap-3 px-6 pt-5 pb-[18px] bg-muted/50 border-b border-border">
                    <FileText className="w-[19px] h-[19px] text-foreground mt-0.5" aria-hidden="true" />
                    <div className="flex-1 min-w-0">
                        <DialogTitle className="m-0 text-[19px] font-semibold tracking-[-0.02em] text-foreground">Data Import Wizard</DialogTitle>
                        <DialogDescription className="mt-1.5 text-[13px] text-muted-foreground">
                            Bring contacts and companies into Imports with field mapping and duplicate handling.
                        </DialogDescription>
                    </div>
                    <button type="button" onClick={close} aria-label="Close" disabled={busy}
                            className="shrink-0 w-7 h-7 border border-input rounded-full flex items-center justify-center text-ink-2 hover:bg-accent">
                        <X className="w-3.5 h-3.5" />
                    </button>
                </div>

                <ol className="shrink-0 flex items-center px-6 py-[18px] border-b border-border" aria-label="Import steps">
                    {STEPS.map((label, i) => {
                        const n = i + 1;
                        const active = step === n;
                        const reachable = n < step;
                        return (
                            <li key={label} className={`flex items-center ${i < 4 ? 'flex-1 min-w-0' : 'flex-none'}`}>
                                <button type="button" disabled={!reachable} onClick={() => reachable && setStep(n)}
                                        aria-current={active ? 'step' : undefined}
                                        className={`inline-flex items-center gap-2 h-8 px-3.5 rounded-full text-[12.5px] font-semibold whitespace-nowrap ${active ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground'} ${reachable ? 'cursor-pointer' : 'cursor-default'}`}>
                                    <span className={active ? 'text-primary-foreground/75' : 'text-muted-foreground'}>{n}</span>{label}
                                </button>
                                {i < 4 && <span className="flex-1 h-px bg-border mx-2.5" />}
                            </li>
                        );
                    })}
                </ol>

                <div className="flex-1 min-h-0 overflow-y-auto px-6 pt-[22px] pb-7">
                    {error && <div role="alert" className="mb-4 px-3.5 py-2.5 rounded-[10px] bg-red-50 dark:bg-bad-bg text-[12.5px] text-red-800 dark:text-bad">{error}</div>}

                    {step === 1 && (
                        <div data-testid="lm-wizard-step-1">
                            <div className="text-[15px] font-semibold text-foreground">Import</div>
                            <div className="mt-1 text-[12.5px] text-muted-foreground">Choose where the records come from.</div>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-[18px]" role="radiogroup" aria-label="Source type">
                                {[['csv', 'Upload CSV', 'Drop a spreadsheet from your computer', Upload],
                                  ['source', 'Third-party source', 'Pull from a connected integration', Layers],
                                  ...(canMigrateOwners
                                      ? [['migration', 'One-time owner migration', 'Move existing contacts to their owners from an exported sheet', Users]]
                                      : [])].map(([key, label, desc, Icon]) => (
                                    <button type="button" key={key} role="radio" aria-checked={mode === key} onClick={() => setMode(key)}
                                            className={`flex items-center gap-3 px-4 py-3.5 rounded-xl text-left border ${mode === key ? 'border-primary bg-muted/50' : 'border-input bg-card'}`}>
                                        <Radio on={mode === key} />
                                        <Icon className="w-[18px] h-[18px] text-ink-2" aria-hidden="true" />
                                        <div className="min-w-0">
                                            <div className="text-[13.5px] font-semibold text-foreground">{label}</div>
                                            <div className="text-xs text-muted-foreground mt-0.5">{desc}</div>
                                        </div>
                                    </button>
                                ))}
                            </div>

                            {isMigration && (
                                <div className="mt-4 px-4 py-3.5 border border-border rounded-xl bg-card" data-testid="lm-migration-setup">
                                    {!migration ? (
                                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                                            <label className="flex flex-col gap-1 text-[12.5px] text-foreground">Parts
                                                <input type="number" min="1" max="100" value={migrationForm.parts} aria-label="Number of parts"
                                                       onChange={(e) => setMigrationForm((f) => ({ ...f, parts: e.target.value }))}
                                                       className="h-[34px] px-2 border border-input rounded-lg" />
                                            </label>
                                            <label className="flex flex-col gap-1 text-[12.5px] text-foreground">Total rows
                                                <input type="number" min="0" value={migrationForm.rows} aria-label="Total rows"
                                                       onChange={(e) => setMigrationForm((f) => ({ ...f, rows: e.target.value }))}
                                                       className="h-[34px] px-2 border border-input rounded-lg" />
                                            </label>
                                            <label className="flex flex-col gap-1 text-[12.5px] text-foreground sm:col-span-3">Reason
                                                <input value={migrationForm.reason} maxLength={500} aria-label="Migration reason"
                                                       onChange={(e) => setMigrationForm((f) => ({ ...f, reason: e.target.value }))}
                                                       className="h-[34px] px-2 border border-input rounded-lg" />
                                            </label>
                                            <label className="flex flex-col gap-1 text-[12.5px] text-foreground sm:col-span-2">…or continue migration (id)
                                                <input value={migrationForm.existing} aria-label="Existing migration id"
                                                       onChange={(e) => setMigrationForm((f) => ({ ...f, existing: e.target.value }))}
                                                       className="h-[34px] px-2 border border-input rounded-lg font-mono" />
                                            </label>
                                            <div className="flex items-end">
                                                <button type="button" onClick={startMigration} data-testid="lm-migration-start"
                                                        disabled={busy || (!migrationForm.existing.trim() && !migrationForm.reason.trim())}
                                                        className="h-[34px] px-3 rounded-lg bg-primary text-primary-foreground text-[12.5px] font-semibold disabled:opacity-40">
                                                    {migrationForm.existing.trim() ? 'Continue migration' : 'Start migration'}
                                                </button>
                                            </div>
                                        </div>
                                    ) : (
                                        <div className="flex items-center gap-3 flex-wrap text-[12.5px] text-foreground">
                                            <span>Migration <span className="font-mono">{migration.operation_id}</span></span>
                                            <label className="inline-flex items-center gap-2">Part
                                                <select value={partIndex} onChange={(e) => setPartIndex(Number(e.target.value))} aria-label="Part number"
                                                        className="h-[30px] px-2 border border-input rounded-lg bg-card">
                                                    {Array.from({ length: migration.declared_parts || 1 }, (_, i) => i + 1).map((n) => (
                                                        <option key={n} value={n}>{n} of {migration.declared_parts || 1}</option>
                                                    ))}
                                                </select>
                                            </label>
                                            <span className="text-muted-foreground">Changes owners of existing contacts only, after a different admin approves.</span>
                                        </div>
                                    )}
                                </div>
                            )}

                            {(mode === 'csv' || (isMigration && migration)) && (
                                <div>
                                    <div
                                        className="mt-4 border-[1.5px] border-dashed border-input rounded-[14px] bg-muted/50 p-11 text-center"
                                        onDragOver={(e) => e.preventDefault()}
                                        onDrop={(e) => { e.preventDefault(); onFile(e.dataTransfer.files?.[0]); }}>
                                        <Upload className="w-[26px] h-[26px] mx-auto text-muted-foreground" aria-hidden="true" />
                                        <div className="mt-3 text-sm font-semibold text-foreground">Drop your CSV here</div>
                                        <div className="mt-1 text-[12.5px] text-muted-foreground">
                                            or browse from your computer · max {(catalog?.max_rows || 10000).toLocaleString()} rows
                                        </div>
                                        <input ref={fileRef} type="file" accept=".csv,text/csv,text/plain" className="hidden"
                                               data-testid="lm-wizard-file" aria-label="Choose a CSV file"
                                               onChange={(e) => onFile(e.target.files?.[0])} />
                                        <button type="button" onClick={() => fileRef.current?.click()} disabled={busy}
                                                className="inline-flex items-center gap-2 h-9 mt-4 px-[15px] bg-card border border-input rounded-[9px] text-[13px] font-medium text-foreground hover:bg-accent">
                                            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : null}Choose file
                                        </button>
                                    </div>
                                </div>
                            )}

                            {mode === 'source' && (
                                <div className="flex flex-col gap-2 mt-4" data-testid="lm-wizard-sources">
                                    {sources.filter((s) => s.key !== 'csv').map((s) => {
                                        const Icon = SOURCE_ICON[s.key] || Layers;
                                        const enabled = s.status === 'enabled';
                                        return (
                                            <div key={s.key} data-testid={`lm-source-${s.key}`}
                                                 className={`flex items-center gap-3 px-3.5 py-3 rounded-xl border ${enabled ? 'border-primary bg-muted/50' : 'border-border bg-card'}`}>
                                                <span className={`shrink-0 w-9 h-9 rounded-[10px] inline-flex items-center justify-center bg-indigo-100 dark:bg-indigo-500/20 text-indigo-700 dark:text-indigo-300 ${enabled ? '' : 'opacity-55'}`}>
                                                    <Icon className="w-[17px] h-[17px]" aria-hidden="true" />
                                                </span>
                                                <div className="flex-1 min-w-0">
                                                    <div className="flex items-center gap-2">
                                                        <span className="text-[13.5px] font-semibold text-foreground">{s.name}</span>
                                                        <span className="text-[11px] text-muted-foreground bg-muted rounded px-1.5 py-0.5">{s.category}</span>
                                                    </div>
                                                    <div className="text-xs text-muted-foreground mt-0.5">
                                                        {s.status === 'locked' ? s.locked_message : s.description}
                                                    </div>
                                                </div>
                                                {s.status === 'coming_soon' && (
                                                    <span className="shrink-0 text-xs font-medium text-muted-foreground bg-muted rounded-full px-3 py-1">Coming soon</span>
                                                )}
                                                {s.status === 'locked' && (
                                                    <span className="shrink-0 inline-flex items-center gap-1.5 text-xs font-medium text-amber-800 dark:text-warn bg-amber-50 dark:bg-warn-bg rounded-full px-3 py-1">
                                                        <Lock className="w-3 h-3" aria-hidden="true" />Locked
                                                    </span>
                                                )}
                                                {enabled && s.key === 'pitchbook' && (
                                                    <div className="shrink-0 flex items-center gap-2">
                                                        <select value={pbEntity} onChange={(e) => setPbEntity(e.target.value)} aria-label="PitchBook entity"
                                                                className="h-[30px] px-2 border border-input rounded-lg text-[12.5px] bg-card">
                                                            {/* Only what the licensing approval covers (#777). */}
                                                            {(s.entities?.length ? s.entities : ['companies']).map((e) => (
                                                                <option key={e} value={e}>{e === 'persons' ? 'People' : 'Companies'}</option>
                                                            ))}
                                                        </select>
                                                        <button type="button" onClick={loadPitchbook} disabled={busy}
                                                                className="h-[30px] px-3 border border-input rounded-lg bg-card text-[12.5px] font-medium text-foreground hover:bg-muted">
                                                            Load snapshot
                                                        </button>
                                                    </div>
                                                )}
                                            </div>
                                        );
                                    })}
                                </div>
                            )}

                            {duplicateOf && (
                                <div className="mt-3.5 px-3.5 py-3 border border-border rounded-[11px] bg-amber-50 dark:bg-warn-bg text-[12.5px] text-amber-800 dark:text-warn" role="status">
                                    This exact file was already imported as “{duplicateOf.batch_name || duplicateOf.file_name}”. Nothing new was created.
                                </div>
                            )}

                            {draft && (
                                <div className="flex items-center gap-3 mt-3.5 px-[15px] py-[13px] border border-border rounded-[11px] bg-card" data-testid="lm-wizard-file-card">
                                    <FileText className="w-[17px] h-[17px] text-violet-700 dark:text-violet-300" aria-hidden="true" />
                                    <div className="flex-1 min-w-0">
                                        <div className="text-[13.5px] font-semibold text-foreground truncate">{draft.file_name}</div>
                                        <div className="text-xs text-muted-foreground mt-0.5 tabular-nums">
                                            {draft.row_count?.toLocaleString()} rows detected · {headers.length} columns
                                            {draft.source_checksum ? <> · <span title={draft.source_checksum}>{draft.source_checksum.slice(0, 19)}…</span></> : null}
                                        </div>
                                    </div>
                                    <span className="text-[11.5px] font-semibold text-emerald-800 dark:text-good bg-emerald-50 dark:bg-good-bg rounded-md px-2 py-0.5">Ready</span>
                                </div>
                            )}
                        </div>
                    )}

                    {step === 2 && isMigration && catalog && (
                        <div data-testid="lm-wizard-step-2">
                            <div className="text-[15px] font-semibold text-foreground">Select import fields</div>
                            <div className="mt-1 text-[12.5px] text-muted-foreground">Choose which fields your CSV will contain.</div>
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-x-7 mt-[22px]">
                                <fieldset>
                                    <legend className="w-full text-[11px] font-semibold tracking-[0.07em] text-muted-foreground pb-2.5 border-b border-border">OWNER MIGRATION FIELDS</legend>
                                    {allFields.map((f) => (
                                        <button type="button" key={f.key} role="checkbox" aria-checked={selected.has(f.key)} onClick={() => toggleMigrationField(f.key)}
                                                className="w-full flex items-center gap-[11px] h-[38px] text-left">
                                            <Box state={selected.has(f.key)} />
                                            <span className="text-[13.5px] text-foreground">{f.label}</span>
                                            {f.required && <span className="text-red-800 dark:text-bad text-[13px]" aria-label="required">*</span>}
                                            <span className="flex-1" />
                                            <span className="text-[12.5px] text-muted-foreground truncate max-w-[150px]">{f.example}</span>
                                        </button>
                                    ))}
                                </fieldset>
                            </div>
                        </div>
                    )}

                    {step === 2 && !isMigration && (
                        <div data-testid="lm-wizard-step-2">
                            <div className="text-[15px] font-semibold text-foreground">Select columns to import</div>
                            <div className="mt-1 text-[12.5px] text-muted-foreground tabular-nums">
                                {draft?.file_name} · {headers.length} column{headers.length === 1 ? '' : 's'} found in {(draft?.row_count ?? 0).toLocaleString()} rows.
                                {' '}Untick any column you don’t want in this import. It is left out of Preview and Staging.
                            </div>
                            <div className="flex items-center gap-2.5 flex-wrap mt-4">
                                <label className="relative flex-1 min-w-[220px] max-w-[360px]">
                                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground" aria-hidden="true" />
                                    <input type="search" value={selQuery} onChange={(e) => setSelQuery(e.target.value)} data-testid="lm-select-search"
                                           aria-label="Search columns" placeholder="Search columns..." autoComplete="off"
                                           className="w-full h-9 pl-8 pr-3 border border-input rounded-lg bg-card text-[13px] text-foreground outline-none placeholder:text-muted-foreground focus:border-primary" />
                                </label>
                                <button type="button" data-testid="lm-select-all" onClick={() => setColumnsSelected(visibleColumns.map(({ h }) => h), true)}
                                        disabled={!visibleColumns.length}
                                        className="inline-flex items-center gap-1.5 h-9 px-3 border border-input rounded-lg bg-card text-[12.5px] font-medium text-foreground hover:bg-muted disabled:opacity-45">
                                    <Check className="w-3.5 h-3.5" aria-hidden="true" />{query ? 'Select matching' : 'Select all'}
                                </button>
                                <button type="button" data-testid="lm-select-clear" onClick={() => setColumnsSelected(visibleColumns.map(({ h }) => h), false)}
                                        disabled={!visibleColumns.length}
                                        className="inline-flex items-center gap-1.5 h-9 px-3 border border-input rounded-lg bg-card text-[12.5px] font-medium text-foreground hover:bg-muted disabled:opacity-45">
                                    <X className="w-3.5 h-3.5" aria-hidden="true" />{query ? 'Clear matching' : 'Clear'}
                                </button>
                                <span className="flex-1" />
                                <span className="text-[12.5px] text-ink-2 tabular-nums" data-testid="lm-select-count" aria-live="polite">
                                    {selectedColumns.size} of {headers.length} columns selected
                                </span>
                            </div>
                            <div className="mt-3.5 border border-border rounded-xl overflow-hidden" role="group" aria-label="CSV columns">
                                <div className={`grid ${columnStats ? 'grid-cols-[28px_minmax(120px,1fr)_minmax(0,2fr)_110px]' : 'grid-cols-[28px_minmax(120px,1fr)_minmax(0,2fr)]'} gap-3.5 items-center px-4 py-[11px] bg-muted/50 border-b border-border text-[11px] font-semibold tracking-[0.06em] text-muted-foreground`}>
                                    <button type="button" role="checkbox" aria-checked={headerState} data-testid="lm-select-header-check"
                                            aria-label={query ? 'Select all matching columns' : 'Select all columns'} disabled={!visibleColumns.length}
                                            onClick={() => setColumnsSelected(visibleColumns.map(({ h }) => h), headerState !== true)}
                                            className="inline-flex items-center justify-center w-5 h-5">
                                        <Box state={headerState} />
                                    </button>
                                    <span>CSV COLUMN</span><span>SAMPLE VALUES</span>
                                    {columnStats && <span className="text-right">FILLED IN</span>}
                                </div>
                                {visibleColumns.length === 0 && (
                                    <div className="px-4 py-6 text-center text-[13px] text-muted-foreground">No columns match “{selQuery.trim()}”</div>
                                )}
                                {visibleColumns.map(({ h, index }) => {
                                    const on = selectedColumns.has(h);
                                    const stats = columnStats?.[h];
                                    const samples = sampleValues(h, sampleRows, stats);
                                    return (
                                        <button type="button" key={h} role="checkbox" aria-checked={on} data-testid={`lm-select-col-${index}`}
                                                onClick={() => setColumnsSelected([h], !on)}
                                                className={`w-full grid ${columnStats ? 'grid-cols-[28px_minmax(120px,1fr)_minmax(0,2fr)_110px]' : 'grid-cols-[28px_minmax(120px,1fr)_minmax(0,2fr)]'} gap-3.5 items-center px-4 py-2.5 border-b border-border/60 last:border-b-0 text-left hover:bg-muted/50`}>
                                            <span className="inline-flex items-center justify-center w-5 h-5"><Box state={on} /></span>
                                            <span className={`text-[13px] font-mono truncate ${on ? 'text-foreground' : 'text-muted-foreground'}`}>{h}</span>
                                            <span className={`text-[12.5px] truncate whitespace-pre ${on ? 'text-ink-2' : 'text-muted-foreground'}`}>{samples.join(SAMPLE_SEPARATOR)}</span>
                                            {columnStats && (
                                                <span className="text-[12.5px] text-right tabular-nums text-muted-foreground">
                                                    {stats ? `${(stats.filled ?? 0).toLocaleString()} of ${(draft.row_count ?? 0).toLocaleString()}` : ''}
                                                </span>
                                            )}
                                        </button>
                                    );
                                })}
                            </div>
                            {selectedColumns.size === 0 && (
                                <div className="mt-3 text-[12.5px] text-amber-800 dark:text-warn" role="status">Select at least one column to continue.</div>
                            )}
                        </div>
                    )}

                    {step === 3 && (
                        <div data-testid="lm-wizard-step-3">
                            <div className="text-[15px] font-semibold text-foreground">Map CSV columns to CRM fields</div>
                            {isMigration ? (
                                <div className="mt-1 text-[12.5px] text-muted-foreground">We matched what we could. Review anything marked unmapped — unmapped columns stay source-only and are never written to the CRM.</div>
                            ) : (
                                <div className="mt-1 text-[12.5px] text-muted-foreground tabular-nums" data-testid="lm-map-summary">
                                    {selectedColumns.size} of {headers.length} columns selected · {mappedCount} mapped
                                    {mappedHeaders.length - mappedCount > 0 && (
                                        <> · {mappedHeaders.length - mappedCount} kept as file column{mappedHeaders.length - mappedCount === 1 ? '' : 's'} (visible in List Management, never written to the CRM)</>
                                    )}.
                                </div>
                            )}
                            {createdNote && (
                                <div role="status" data-testid="lm-custom-field-created"
                                     className="mt-3.5 px-3.5 py-2.5 rounded-[10px] bg-emerald-50 dark:bg-good-bg text-[12.5px] text-emerald-800 dark:text-good">{createdNote}</div>
                            )}
                            <div className="mt-[18px] border border-border rounded-xl overflow-hidden">
                                <div className="grid grid-cols-[1fr_1fr_150px] gap-3.5 px-4 py-[11px] bg-muted/50 border-b border-border text-[11px] font-semibold tracking-[0.06em] text-muted-foreground">
                                    <span>CSV COLUMN</span><span>CRM FIELD</span><span>SAMPLE</span>
                                </div>
                                {mappedHeaders.map((h) => (
                                    <div key={h} className="grid grid-cols-[1fr_1fr_150px] gap-3.5 items-center px-4 py-2.5 border-b border-border/60">
                                        <span className="min-w-0">
                                            <span className="block text-[13px] font-mono text-foreground truncate">{h}</span>
                                            {!isMigration && !columns[h] && <span className="block text-[11.5px] text-muted-foreground">Kept as a file column</span>}
                                        </span>
                                        <LmFieldCombobox
                                            ref={(el) => { triggerRefs.current[h] = el; }}
                                            header={h} value={columns[h] || null} groups={fieldGroups} usedBy={usedBy}
                                            onChange={(target) => setColumn(h, target)}
                                            onAddCustom={isMigration ? undefined : () => { addedFromRef.current = h; setAddFieldFor(h); }} />
                                        <span className="text-[12.5px] text-muted-foreground truncate">{sampleRows.map((r) => r[h]).filter(Boolean)[0] || ''}</span>
                                    </div>
                                ))}
                            </div>
                            {!isMigration && (
                                <label className="mt-3.5 inline-flex items-center gap-2 text-[12.5px] text-foreground">
                                    <input type="checkbox" checked={saveDefault} onChange={(e) => setSaveDefault(e.target.checked)} />
                                    Save this column selection and mapping for files with the same columns
                                </label>
                            )}
                            {isMigration && (
                                <div className="mt-3.5 text-[12.5px] text-muted-foreground">
                                    Map Contact ID or Contact email, and exactly one owner column. Nothing else is imported.
                                </div>
                            )}
                        </div>
                    )}

                    {step === 4 && (
                        <div data-testid="lm-wizard-step-4">
                            <div className="text-[15px] font-semibold text-foreground">Preview data</div>
                            {busy && !preview && <div className="mt-6 flex items-center gap-2 text-[13px] text-muted-foreground"><Loader2 className="w-4 h-4 animate-spin" />Checking every row…</div>}
                            {preview && (
                                <>
                                    <div className="mt-1 text-[12.5px] text-muted-foreground tabular-nums">
                                        First {preview.sample_rows.length} of {preview.counts.total.toLocaleString()} rows. {preview.counts.duplicates} possible duplicate{preview.counts.duplicates === 1 ? '' : 's'} found.
                                    </div>
                                    {preview.counts.migration ? (
                                        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2.5 mt-4" data-testid="lm-preview-counts">
                                            <Tile label="MATCHED" value={preview.counts.migration.matched} />
                                            <Tile label="WILL CHANGE" value={preview.counts.migration.changed} />
                                            <Tile label="ALREADY CORRECT" value={preview.counts.migration.already_correct} />
                                            <Tile label="EXCEPTIONS" value={preview.counts.invalid} />
                                        </div>
                                    ) : (
                                        <div className="grid grid-cols-2 sm:grid-cols-5 gap-2.5 mt-4" data-testid="lm-preview-counts">
                                            <Tile label="NEW" value={preview.counts.new} />
                                            <Tile label="MATCHED" value={preview.counts.matched} />
                                            <Tile label="POSSIBLE" value={preview.counts.possible} />
                                            <Tile label="AMBIGUOUS" value={preview.counts.ambiguous} />
                                            <Tile label="NEEDS REVIEW" value={preview.counts.invalid} />
                                        </div>
                                    )}
                                    {preview.warnings.length > 0 && (
                                        <ul className="mt-4 space-y-1.5" aria-label="Warnings">
                                            {preview.warnings.map((w, i) => (
                                                <li key={i} data-testid={`lm-preview-warning-${w.code}`} className={`px-3 py-2 rounded-lg text-[12.5px] ${w.blocking ? 'bg-red-50 dark:bg-bad-bg text-red-800 dark:text-bad' : 'bg-amber-50 dark:bg-warn-bg text-amber-800 dark:text-warn'}`}>{warningText(w, fieldLabel)}</li>
                                            ))}
                                        </ul>
                                    )}
                                    {preview.normalizations.length > 0 && (
                                        <div className="mt-4" data-testid="lm-preview-normalizations">
                                            <div className="text-[13px] font-semibold text-foreground">Normalized values</div>
                                            <div className="flex flex-wrap gap-1.5 mt-2">
                                                {preview.normalizations.map((n, i) => (
                                                    <span key={i} className="text-xs px-2 py-1 rounded-md bg-muted text-ink-2">
                                                        {fieldLabel[n.field] || n.field}: {n.from} → {n.to || 'not imported'} <span className="text-muted-foreground tabular-nums">×{n.count}</span>
                                                    </span>
                                                ))}
                                            </div>
                                        </div>
                                    )}
                                    {isMigration ? (
                                        preview.unmapped_columns.length > 0 && (
                                            <div className="mt-3 text-[12.5px] text-muted-foreground">
                                                Unmapped (source-only, read-only): {preview.unmapped_columns.join(', ')}
                                            </div>
                                        )
                                    ) : (
                                        <div className="mt-4 flex items-center gap-3">
                                            <div className="flex-1 min-w-0 text-[12.5px] text-muted-foreground" data-testid="lm-preview-columns-note">
                                                Showing {Math.min(INLINE_PREVIEW_COLUMNS, previewColumns.length)} of {previewColumns.length} columns
                                                {preview.unmapped_columns.length > 0 && (
                                                    <> · Kept as file columns, not written to the CRM: {preview.unmapped_columns.join(', ')}</>
                                                )}
                                            </div>
                                            {previewColumns.length > INLINE_PREVIEW_COLUMNS && (
                                                <LmPreviewExpanded columns={previewColumns} rows={preview.sample_rows} fileName={draft?.file_name} />
                                            )}
                                        </div>
                                    )}
                                    <PreviewTable columns={previewColumns.slice(0, INLINE_PREVIEW_COLUMNS)} rows={preview.sample_rows} />
                                </>
                            )}
                            {!isMigration && (<>
                            <div className="mt-5 text-[13px] font-semibold text-foreground">Duplicate handling</div>
                            <div className="flex flex-col gap-2 mt-2.5" role="radiogroup" aria-label="Duplicate handling">
                                {DUPLICATE_OPTIONS.map(([key, label, desc]) => (
                                    <div key={key}>
                                        <button type="button" role="radio" aria-checked={dupPolicy === key} disabled={busy}
                                                onClick={() => { setDupPolicy(key); runPreview({ dupPolicy: key }); }}
                                                className={`w-full flex items-start gap-[11px] px-3.5 py-3 rounded-[11px] text-left border ${dupPolicy === key ? 'border-primary bg-muted/50' : 'border-border bg-card'}`}>
                                            <span className="mt-0.5"><Radio on={dupPolicy === key} /></span>
                                            <div>
                                                <div className="text-[13px] font-medium text-foreground">{label}</div>
                                                <div className="text-xs text-muted-foreground mt-0.5">{desc}</div>
                                            </div>
                                        </button>
                                        {key === 'update' && dupPolicy === 'update' && (
                                            <div className="ml-9 mt-2 flex flex-col gap-1.5" role="radiogroup" aria-label="Write policy">
                                                {[['fill_blank', 'Fill blank fields only (recommended) — never overwrite a value already in the CRM'],
                                                  ['overwrite', 'Overwrite non-empty fields with the import’s values']].map(([wk, wl]) => (
                                                    <label key={wk} className="inline-flex items-center gap-2 text-[12.5px] text-foreground">
                                                        <input type="radio" name="lm-write-policy" checked={writePolicy === wk} disabled={busy}
                                                               onChange={() => { setWritePolicy(wk); runPreview({ writePolicy: wk }); }} />
                                                        {wl}
                                                    </label>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                ))}
                            </div>
                            </>)}
                        </div>
                    )}

                    {step === 5 && preview && (
                        <div className="py-[26px]" data-testid="lm-wizard-step-5">
                            <div className="max-w-[520px] mx-auto text-center">
                                <span className="inline-flex items-center justify-center w-[52px] h-[52px] rounded-full bg-emerald-50 dark:bg-good-bg text-emerald-800 dark:text-good">
                                    <CheckCircle2 className="w-[26px] h-[26px]" aria-hidden="true" />
                                </span>
                                <div className="mt-4 text-[18px] font-semibold tracking-[-0.02em] text-foreground">
                                    Ready to import {preview.counts.total.toLocaleString()} records
                                </div>
                                <div className="mt-1.5 text-[13px] text-muted-foreground">Records land in Imports as read-only. Move them to Staging to clean them up.</div>
                                <div className="grid grid-cols-3 gap-2.5 mt-[22px] text-left">
                                    <Tile label="RECORDS" value={preview.counts.total.toLocaleString()} />
                                    {isMigration
                                        ? <Tile label="FIELDS" value={selected.size} />
                                        : <Tile label="COLUMNS" value={mappedCount} />}
                                    <Tile label="DUPLICATES" value={preview.counts.duplicates} />
                                </div>
                                <label className="flex flex-col gap-2 mt-5 text-left">
                                    <span className="text-[12.5px] font-medium text-foreground">Batch name</span>
                                    <input value={batchName} onChange={(e) => setBatchName(e.target.value)} maxLength={120} data-testid="lm-batch-name"
                                           className="h-[38px] px-3 border border-input rounded-[9px] text-[13px] text-foreground outline-none focus:border-primary" />
                                </label>
                            </div>
                        </div>
                    )}
                </div>

                <div className="shrink-0 flex items-center gap-2.5 px-6 py-4 border-t border-border bg-muted/50">
                    {step > 1 && (
                        <button type="button" onClick={() => setStep((s) => Math.max(1, s - 1))} disabled={busy}
                                className="inline-flex items-center gap-2 h-[38px] px-[15px] bg-card border border-input rounded-[9px] text-[13px] font-medium text-foreground hover:bg-accent">
                            <ArrowLeft className="w-[15px] h-[15px]" aria-hidden="true" />Back
                        </button>
                    )}
                    <div className="flex-1" />
                    <button type="button" onClick={next} disabled={!canNext || busy} data-testid="lm-wizard-next"
                            className="inline-flex items-center gap-2 h-[38px] px-4 bg-primary hover:bg-primary/90 text-primary-foreground rounded-[9px] text-[13px] font-semibold disabled:opacity-40">
                        {busy && step >= 4 ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
                        {NEXT_LABEL[step]}
                        {step === 5 ? <Check className="w-[15px] h-[15px]" aria-hidden="true" /> : <ArrowRight className="w-[15px] h-[15px]" aria-hidden="true" />}
                    </button>
                </div>
            </DialogContent>
            {!isMigration && (
                <LmAddCustomFieldDialog
                    open={!!addFieldFor} header={addFieldFor} stats={columnStats?.[addFieldFor]} catalog={catalog}
                    usedBy={usedBy} api={api} importId={draft?.import_id}
                    onCreated={onCustomFieldCreated} onMapExisting={mapExistingField}
                    onClose={() => setAddFieldFor(null)}
                    onCloseAutoFocus={() => triggerRefs.current[addedFromRef.current]?.focus()} />
            )}
        </Dialog>
    );
}
